"use client";
import AiGridArea from "@/components/ai/AiGridArea";
import EnterpriseTop from "@/components/ai/EnterpriseTop";
import WorkSpentSearch from "@/components/ai/WorkSpentSearch";
import AiToolsFaq, {
  IFaqList,
} from "@/components/tools/ToolsCommon/AiToolsFaq";
import { StaticImageData } from "next/image";
import { useRef } from "react";
import EnterpriseWorkProcess from "@/components/ai/EnterpriseWorkProcess";
import SearchCalculator from "@/components/ai/SearchCalculator";
import EnterpriseCtaBox from "@/components/ai/EnterpriseCtaBox";
import ProvenBuilt from "@/components/ai/ProvenBuilt";
import { BookDemoHubspotButtonUrl } from "@/utils/hubspot-contants";
import WhyKroolo from "@/components/ai/WhyKroolo";
import BuiltTabArea from "@/components/ai/BuiltTab/BuiltTabArea";
import AdBanner from "@/components/Advertisement/AdBanner";
import AnswerTabs from "@/components/ai/AnswerTabs";
import Connectors from "@/components/ai/Connectors";

export interface ItemInfoProps {
  introText: React.ReactNode;
  descriptionText: string;
  bannerImage: string;
  featuredVideoJson: object;
  buttonUrl?: string;
}

export type ToolAiGridAreaType = {
  contextualTag: string;
  text: string;
  subtext?: string;
  stepsData?: string[];
  image: StaticImageData;
  featuredVideoJson?: object;
};

export interface MetricCard {
  id: number;
  title: string;
  value: string;
  icon: StaticImageData;
}
export interface WhyKrooloInfo {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

export interface WhyKrooloCard {
  id: number;
  title: string;
  description: string;
  icon: StaticImageData;
}
type TFeature = {
  feature: string;
  kroolo: boolean;
  others: boolean;
};

export interface BuiltTabData {
  intro: string;
  title: string;
  description: string;
  featureImg: StaticImageData;
  featureList: string[];
}

export type TEnterpriseSearchProps = {
  params: {
    itemInfo: ItemInfoProps;
    gridAreaInfo: ToolAiGridAreaType[];
    provenBuiltMetrics: MetricCard[];
    whyKrooloInfo: WhyKrooloInfo[];
    whyKroolo: WhyKrooloCard[];
    faqList: IFaqList[];
    compareEnterpriseFeatures: TFeature[];
    builtTabData: BuiltTabData[];
  };
};

const EnterpriseSearchPageRoot = ({ params }: TEnterpriseSearchProps) => {
  const featuresRef = useRef<HTMLDivElement | null>(null);

  const scrollToFeatures = () => {
    if (featuresRef.current) {
      const elementTop = featuresRef.current.offsetTop;
      window.scrollTo({
        top: elementTop - 50,
        behavior: "smooth",
      });
    }
  };
  const {
    itemInfo,
    gridAreaInfo,
    faqList,
    compareEnterpriseFeatures,
    builtTabData,
    provenBuiltMetrics,
    whyKrooloInfo,
    whyKroolo,
  } = params;

  return (
    <>
      <EnterpriseTop itemInfo={itemInfo} scrollToFeatures={scrollToFeatures} />
      <WorkSpentSearch />
      <ProvenBuilt metrics={provenBuiltMetrics} />
      <SearchCalculator />
      <AnswerTabs />
      <Connectors />
      <EnterpriseWorkProcess />
      <WhyKroolo whyKrooloInfo={whyKrooloInfo} whyKroolo={whyKroolo} />
      <AiGridArea gridAreaInfo={gridAreaInfo} ref={featuresRef} />
      <BuiltTabArea builtTabData={builtTabData} />
      <EnterpriseCtaBox />
      <AiToolsFaq
        faqTitle="Frequently Asked Questions about Kroolo Enterprise Search"
        faqList={faqList}
      />
      <AdBanner
        title={<>Get Started with
          <br />
          <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
            Kroolo Enterprise
          </span>
        </>}
        subtitle="Ready to transform how your enterprise teams search, collaborate, and manage knowledge? Our enterprise specialists will work with you to design a solution that meets your specific requirements."
        btnText="Schedule Enterprise Demo"
        url={BookDemoHubspotButtonUrl}
      />
    </>
  );
};

export default EnterpriseSearchPageRoot;
